import { useState } from 'react'

/**
 * Password field with a show/hide eye toggle (Tej 2026-08-03) — the shared
 * control for every password input on both shells (desktop SignIn/Register,
 * mobile SignInScreen/RegisterScreen). Born from a real support moment: a
 * mistyped password on a phone keyboard is invisible without this.
 *
 * The value handling + data-testid stay on the <input> itself so existing
 * QA flows (browser-accounts.mjs fills signin-password) keep working. The
 * toggle is a 44px-wide in-field button — type="button" so it can never
 * submit the form.
 */
export function PasswordInput({
  value,
  onChange,
  placeholder = 'Password',
  autoComplete = 'current-password',
  required = false,
  minLength,
  className = 'input h-[52px] text-[15px]',
  testId,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoComplete?: string
  required?: boolean
  minLength?: number
  className?: string
  testId?: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${className} w-full pr-12`}
        required={required}
        minLength={minLength}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        aria-pressed={show}
        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-soft hover:text-ink focus-visible:text-ink transition-colors"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {show ? (
          // eye-off
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" />
            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </svg>
        ) : (
          // eye
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  )
}
